import request from '@/utils/request'
import axios from 'axios'
import { getApiBaseUrl } from '@/utils/apiBaseUrl'

const LOGIN_PUBLIC_KEY_URL = '/api/v1/auth/login/public-key'
const LOGIN_KEY_EXPIRED_MESSAGE = '登录信息已过期，请刷新页面后重试。'
let loginPublicKeyPromise = null
const TRUTHY_VALUES = ['1', 'true', 'yes', 'on']

const isRsaLoginEnabled = () => {
  const flag = String(process.env.VUE_APP_LOGIN_RSA_ENABLED || '').trim().toLowerCase()
  return TRUTHY_VALUES.includes(flag)
}

const clearLoginPublicKeyCache = () => {
  loginPublicKeyPromise = null
}

const getLoginPublicKey = async () => {
  if (!loginPublicKeyPromise) {
    loginPublicKeyPromise = request({
      method: 'GET',
      url: LOGIN_PUBLIC_KEY_URL,
      noErrorNotification: true
    })
      .then(response => response?.data || response)
      .catch(error => {
        loginPublicKeyPromise = null
        throw error
      })
  }
  return loginPublicKeyPromise
}

const getLoginErrorMessage = (error) => {
  const detail = error?.response?.data?.detail || error?.data?.detail || error?.detail
  if (typeof detail === 'object' && detail !== null) {
    return detail.message || ''
  }
  return detail || error?.response?.data?.message || error?.data?.message || error?.message || ''
}

const isLoginKeyExpiredError = (error) => {
  const errMsg = String(getLoginErrorMessage(error))
  return errMsg.includes(LOGIN_KEY_EXPIRED_MESSAGE) || errMsg.includes('解密失败') || errMsg.includes('过期')
}

const isSecureLoginUnsupportedError = (error) => {
  return String(error?.message || error || '').includes('当前浏览器不支持安全登录')
}

const pemToArrayBuffer = (pem) => {
  const base64 = String(pem || '')
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s/g, '')

  if (!base64) {
    throw new Error('登录安全初始化失败，请刷新后重试')
  }

  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

const canUseSecureLoginEncryption = () => {
  return Boolean(window.isSecureContext && window.crypto?.subtle && typeof TextEncoder !== 'undefined')
}

const encryptLoginPassword = async (password) => {
  if (!canUseSecureLoginEncryption()) {
    return null
  }
  const subtle = window.crypto.subtle

  const { public_key: publicKey, key_id: keyId } = await getLoginPublicKey()
  if (!keyId) {
    clearLoginPublicKeyCache()
    throw new Error('登录安全初始化失败，请刷新后重试')
  }
  const cryptoKey = await subtle.importKey(
    'spki',
    pemToArrayBuffer(publicKey),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['encrypt']
  )

  const encrypted = await subtle.encrypt(
    { name: 'RSA-OAEP' },
    cryptoKey,
    new TextEncoder().encode(password)
  )
  return {
    encryptedPassword: arrayBufferToBase64(encrypted),
    keyId
  }
}

export async function login(data) {
  return loginWithEncryptedPassword(data, true)
}

function normalizeLoginResponse(response, fallbackUsername) {
  const loggedUsername = fallbackUsername || '未知用户';
  const responseData = response.data || response || {};
  const token = findToken(responseData);
  if (!token) {
    console.error('登录响应中未找到有效token:', responseData);
    return Promise.reject(new Error('登录成功但未返回有效的访问令牌'));
  }

  if (responseData.user_info && !responseData.user) {
    responseData.user = responseData.user_info;
  }

  if (responseData.current_tenant) {
    localStorage.setItem('current_tenant', JSON.stringify({
      id: responseData.current_tenant.id,
      name: responseData.current_tenant.name,
      code: responseData.current_tenant.code,
      role: responseData.current_tenant.role
    }));
  }

  if (!responseData.user && token) {
    console.warn('登录成功但user对象为null，创建基本用户信息');
    responseData.user = {
      username: loggedUsername,
      id: extractUserIdFromToken(token),
      role: null
    };
  }

  return {
    access_token: token,
    token_type: responseData.token_type || "bearer",
    user: responseData.user || null,
    tenants: responseData.tenants || [],
    current_tenant: responseData.current_tenant || null,
    needs_tenant_selection: responseData.needs_tenant_selection || false,
    user_info: responseData.user || responseData.user_info || null,
    session_id: responseData.session_id || null,
    tenant_id: responseData.tenant_id || null
  };
}

async function loginWithEncryptedPassword(data, retryOnKeyExpired) {
  // 确定API URL
  const apiUrl = `/api/v1/auth/login`

  // 验证登录参数
  if (!data.username) {
    return Promise.reject(new Error('用户名不能为空'))
  }
  
  if (!data.password) {
    return Promise.reject(new Error('密码不能为空'))
  }

  // 默认禁用应用层 RSA 登录加密，仅在显式开关开启时启用。
  if (!isRsaLoginEnabled()) {
    clearLoginPublicKeyCache()
    return loginWithPlainPasswordFallback(data)
  }

  let encryptedPassword
  let keyId
  try {
    const encrypted = await encryptLoginPassword(data.password)
    if (!encrypted) {
      return loginWithPlainPasswordFallback(data)
    }
    encryptedPassword = encrypted.encryptedPassword
    keyId = encrypted.keyId
  } catch (error) {
    if (isSecureLoginUnsupportedError(error)) {
      console.warn('当前浏览器不支持 WebCrypto 登录加密，回退到后端兼容登录')
      return loginWithPlainPasswordFallback(data)
    }
    throw error
  }

  // 使用 application/x-www-form-urlencoded 格式（OAuth2 标准，兼容性更好，避免 FormData boundary 解析问题）
  const params = new URLSearchParams()
  params.append('username', data.username)
  if (encryptedPassword && keyId) {
    params.append('encrypted_password', encryptedPassword)
    params.append('password_key_id', keyId)
  } else {
    // HTTP 或浏览器能力不足时，降级为明文密码提交；后端需开启 ALLOW_PLAIN_LOGIN_PASSWORD。
    params.append('password', data.password)
  }
  if (data.login_type) {
    params.append('login_type', data.login_type)
  }
  if (data.captcha_id) {
    params.append('captcha_id', data.captcha_id)
  }
  if (data.captcha_code) {
    params.append('captcha_code', data.captcha_code)
  }
  if (data.tenant_code) {
    params.append('tenant_code', data.tenant_code)
  }

  return request({
    method: 'POST',
    url: apiUrl,
    data: params,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    },
    timeout: 15000, // 15秒超时
    noErrorNotification: true, // 避免密钥过期重试时触发全局错误弹窗
    validateStatus: function (status) {
      return status >= 200 && status < 300; // 只接受2xx的状态码
    }
  })
  .then(response => {
    return normalizeLoginResponse(response, data.username)
  })
  .catch(error => {
    if (retryOnKeyExpired && isLoginKeyExpiredError(error)) {
      clearLoginPublicKeyCache()
      return loginWithEncryptedPassword(data, false)
    }
    throw error
  })
}

async function loginWithPlainPasswordFallback(data) {
  const apiUrl = `/api/v1/auth/login`
  const payload = {
    username: data.username,
    password: data.password
  }
  if (data.login_type) {
    payload.login_type = data.login_type
  }
  if (data.captcha_id) {
    payload.captcha_id = data.captcha_id
  }
  if (data.captcha_code) {
    payload.captcha_code = data.captcha_code
  }
  if (data.tenant_code) {
    payload.tenant_code = data.tenant_code
  }

  return request({
    method: 'POST',
    url: apiUrl,
    data: payload,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    timeout: 15000,
    noErrorNotification: true, // 避免登录失败时触发全局错误弹窗，由调用方(如Login.vue)统一处理
    validateStatus: function (status) {
      return status >= 200 && status < 300
    }
  }).then(response => normalizeLoginResponse(response, data.username))
}

// 从响应中查找token，尝试多种可能的字段名和位置
function findToken(response) {
  // 直接检查常见的token字段名
  const possibleTokenFields = [
    'access_token', 
    'token', 
    'accessToken', 
    'auth_token', 
    'authToken', 
    'jwt', 
    'id_token'
  ];
  
  // 检查顶层字段
  for (const field of possibleTokenFields) {
    if (response[field] && typeof response[field] === 'string' && response[field].length > 20) {
      return response[field];
    }
  }
  
  // 检查嵌套在data字段中
  if (response.data && typeof response.data === 'object') {
    for (const field of possibleTokenFields) {
      if (response.data[field] && typeof response.data[field] === 'string' && response.data[field].length > 20) {
        return response.data[field];
      }
    }
  }
  
  // 检查嵌套在result字段中
  if (response.result && typeof response.result === 'object') {
    for (const field of possibleTokenFields) {
      if (response.result[field] && typeof response.result[field] === 'string' && response.result[field].length > 20) {
        return response.result[field];
      }
    }
  }
  
  // 检查嵌套在其他可能的容器字段中
  const possibleContainers = ['data', 'result', 'response', 'body', 'payload'];
  for (const container of possibleContainers) {
    if (response[container] && typeof response[container] === 'object') {
      for (const field of possibleTokenFields) {
        if (response[container][field] && typeof response[container][field] === 'string' && response[container][field].length > 20) {
          return response[container][field];
        }
      }
    }
  }
  
  // 如果以上都没找到，尝试查找任何看起来像JWT的字符串
  // JWT通常是三段由点分隔的base64编码字符串
  const jwtPattern = /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.[A-Za-z0-9-_.+/=]*$/;
  for (const key in response) {
    if (typeof response[key] === 'string' && response[key].length > 20 && jwtPattern.test(response[key])) {
      return response[key];
    }
  }
  
  // 未找到任何token
  return null;
}

// 从JWT token中提取用户ID
function extractUserIdFromToken(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const payload = JSON.parse(jsonPayload);
    return payload.sub || payload.user_id || payload.id || null;
  } catch (e) {
    console.error('从token提取用户ID失败:', e);
    return null;
  }
}

export function getInfo() {
  return request({
    url: '/api/v1/auth/current-user',
    method: 'get',
    retry: 2,
    retryDelay: 1000,
    timeout: 15000  // 统一超时时间为15秒
  })
}

export function getLoginCaptcha() {
  return request({
    url: '/api/v1/auth/login/captcha',
    method: 'get',
    headers: {
      'X-No-Auth': 'true'
    },
    isPublicApi: true,
    noAuth: true
  })
}

export async function logout() {
  try {
    // ✅ 获取会话ID
    const sessionId = localStorage.getItem('session_id')
    
    const response = await request({
      url: '/api/v1/auth/logout',
      method: 'post',
      retry: 1,
      retryDelay: 1000,
      timeout: 5000,
      headers: sessionId ? {
        'X-Session-Id': sessionId
      } : {}
    });
    
    // ✅ 清除会话ID
    if (sessionId) {
      localStorage.removeItem('session_id')
    }
    
    return response;
  } catch (error) {
    console.warn('登出接口调用失败:', error);
    // 区分网络错误和业务错误
    if (error.isAxiosError) {
      console.error('网络请求失败:', error.message);
    } else {
      console.error('业务处理失败:', error.message);
    }
    
    // ✅ 即使失败也清除会话ID
    localStorage.removeItem('session_id')
    
    // 返回一个带有状态标识的响应
    return {
      success: false,
      message: '登出成功（本地）',
      error: error.message
    };
  }
}

// 飞书登录相关API
export function checkFeishuAuth() {
  return request({
    url: '/api/v1/auth/feishu/check',
    method: 'get'
  })
}

/**
 * 飞书登录接口
 * @param {string} tenantCode - 租户代码
 * @return {Promise} - 返回Promise对象
 */
export function feishuLogin(tenantCode) {
  console.log(`[飞书登录API] 开始请求，参数: tenantCode=${tenantCode}, 时间戳=${new Date().toISOString()}`);
  
  // 构建请求参数
  const params = { 
    return_to: window.location.origin + '/#/login/callback' 
  };
  
  // 飞书登录不再需要预先指定租户代码
  if (tenantCode) {
    params.tenant_code = tenantCode;
    console.log(`[飞书登录API] 添加租户代码: ${tenantCode}`);
  } else {
    console.log('[飞书登录API] 不指定租户代码，由后端处理用户可访问的租户');
  }
  
  console.log(`[飞书登录API] 请求参数:`, params);
  
  return request({
    url: '/api/v1/auth/feishu/login',
    method: 'get',
    params: params,
    // 确保不需要认证就可以访问这个接口
    headers: {
      'X-No-Auth': 'true'
    },
    isPublicApi: true,
    noAuth: true,
    retry: 1,
    retryDelay: 1000,
    timeout: 10000,
    showAuthError: false
  });
}

export function handleFeishuCallback(code, state) {
  // 构建飞书回调URL
  const apiBaseUrl = getApiBaseUrl();
  const callbackUrl = `${apiBaseUrl}/api/v1/auth/feishu/callback?code=${code}&state=${encodeURIComponent(state || '')}`;
  
  console.log(`[飞书回调API] 开始处理回调，code=${code.substring(0, 5)}...，state=${state ? state.substring(0, 10) + '...' : '无'}`);
  
  // 使用fetch处理，更好地处理重定向响应
  return fetch(callbackUrl, {
    method: 'GET',
    redirect: 'follow', // 允许浏览器跟随重定向
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Accept': 'application/json'
    }
  }).then(response => {
    // 检查是否是重定向响应
    if (response.redirected) {
      console.log('飞书回调返回重定向URL:', response.url);
      
      // 检查URL是否包含错误信息
      const redirectUrl = new URL(response.url);
      const hasError = redirectUrl.hash.includes('error=');
      
      if (hasError) {
        console.warn('飞书回调重定向包含错误信息:', redirectUrl.hash);
      }
      
      return { redirectUrl: response.url };
    }
    
    // 否则尝试解析JSON响应
    return response.json().then(data => {
      console.log('飞书回调处理成功:', data);
      return data;
    }).catch(error => {
      console.error('解析飞书回调JSON响应失败:', error);
      
      // 如果无法解析JSON，尝试获取文本内容
      return response.text().then(text => {
        console.log('飞书回调原始响应:', text);
        throw new Error('无法解析飞书登录响应');
      });
    });
  }).catch(error => {
    console.error('飞书回调处理失败:', error);
    throw new Error('飞书登录请求失败，请重试');
  });
}

export function feishuAutoLogin(code) {
  return request({
    url: `/api/v1/auth/feishu/autologin?code=${code}`,
    method: 'get'
  }).then(response => {
    console.log('飞书自动登录成功:', response.data);
    return response.data;
  }).catch(error => {
    console.error('飞书自动登录失败:', error);
    throw new Error('飞书自动登录失败，请重试');
  });
}

// 添加一个函数来获取完整的API URL
export function getFullApiUrl(path) {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${path}`;
}

/**
 * 切换当前用户的租户
 * @param {number|string} tenantIdOrCode - 租户ID或租户代码
 * @param {boolean} isCode - 是否是租户代码
 * @return {Promise} - 返回Promise对象
 */
export function switchTenant(tenantIdOrCode, isCode = false) {
  console.log(`[租户切换API] 开始请求切换租户，目标租户: ${tenantIdOrCode}, 类型: ${isCode ? '代码' : 'ID'}`);
  
  if (!tenantIdOrCode) {
    return Promise.reject(new Error('未提供租户ID或代码'));
  }
  
  const data = isCode 
    ? { tenant_code: tenantIdOrCode } 
    : { tenant_id: parseInt(tenantIdOrCode, 10) };
  
  return request({
    url: '/api/v1/auth/switch-tenant',
    method: 'post',
    data: data
  }).then(response => {
    console.log(`[租户切换API] 租户切换成功:`, response.data);
    
    // 保存新令牌
    if (response.data.access_token) {
      const token = response.data.access_token;
      localStorage.setItem('token', token);
    }
    
    // 保存新的当前租户信息
    if (response.data.current_tenant) {
      localStorage.setItem('current_tenant', JSON.stringify({
        id: response.data.current_tenant.id,
        name: response.data.current_tenant.name,
        code: response.data.current_tenant.code,
        role: response.data.current_tenant.role
      }));
    }
    
    return response.data;
  }).catch(error => {
    console.error(`[租户切换API] 租户切换失败:`, error);
    throw error;
  });
}

// 获取飞书登录URL的API函数
export function getFeishuAuthUrl(tenant_code = 'default', email = null) {
  // 构建请求参数
  const params = new URLSearchParams();
  if (tenant_code) {
    params.append('tenant_code', tenant_code);
  }
  if (email) {
    params.append('email', email);
  }

  // 获取当前页面URL作为回调地址参数
  const callbackUrl = encodeURIComponent(window.location.origin + '/#/login/callback');
  params.append('redirect_url', callbackUrl);
  
  // 请求后端获取飞书授权URL
  return request({
    url: `/api/v1/auth/feishu/authorize?${params.toString()}`,
    method: 'GET'
  });
}

// 验证邀请码
export function validateInvitationCodeApi(code) {
  return request({
    url: `/api/v1/register/validate-invitation`,
    method: 'POST',
    params: { code }
  })
}

// 检查用户名是否可用
export function checkUsernameAvailabilityApi(username) {
  return request({
    url: `/api/v1/register/check-username`,
    method: 'GET',
    params: { username },
    customErrorHandling: true
  })
}

// 检查手机号是否可用
export function checkPhoneAvailabilityApi(phone) {
  return request({
    url: `/api/v1/register/check-phone`,
    method: 'GET',
    params: { phone },
    customErrorHandling: true
  })
}

// 用户注册
export function registerUserApi(data) {
  return request({
    url: `/api/v1/register/`,
    method: 'POST',
    data: data,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

// 忘记密码 - 发送重置邮件
export function forgotPassword(email) {
  return request({
    url: `/api/v1/auth/forgot-password`,
    method: 'POST',
    data: { email },
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

// 通过token重置密码
export function resetPasswordWithToken(data) {
  return request({
    url: `/api/v1/auth/reset-password-with-token`,
    method: 'POST',
    data: data,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

// 忘记密码 - 发送短信验证码，需携带图形验证码信息
export function sendPasswordResetSms(data) {
  return request({
    url: `/api/v1/auth/password-reset/sms/send`,
    method: 'POST',
    data,
    headers: {
      'Content-Type': 'application/json'
    },
    isPublicApi: true,
    noAuth: true
  })
}

export function confirmPasswordResetSms(data) {
  return request({
    url: `/api/v1/auth/password-reset/sms/confirm`,
    method: 'POST',
    data,
    headers: {
      'Content-Type': 'application/json'
    },
    isPublicApi: true,
    noAuth: true
  })
}

// 发送邮箱验证码
export function sendVerificationCode(email) {
  return request({
    url: `/api/v1/auth/send-verification-code`,
    method: 'POST',
    data: { email },
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

// 验证邮箱验证码
export function verifyEmailCode(email, code) {
  return request({
    url: `/api/v1/auth/verify-email-code`,
    method: 'POST',
    data: { email, code },
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

// 修改密码 - 需要当前密码验证
export function changePassword(currentPassword, newPassword) {
  const formData = new FormData()
  formData.append('current_password', currentPassword)
  formData.append('new_password', newPassword)
  
  return request({
    url: `/api/v1/auth/reset-password`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}
